<template>
  <div class="p-6">
    <!-- Header with Student Name and Actions -->
    <div v-if="studentReport" class="flex justify-between items-center mb-6 print-hide">
      <div>
        <button @click="goBack" class="text-blue-600 hover:underline mb-2">&larr; Back to Test Details</button>
        <h1 class="text-3xl font-bold text-gray-800">Detailed Report for {{ studentReport.name }}</h1>
      </div>
      <div class="flex items-center space-x-2">
        <button @click="recheckStudent" class="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition" :disabled="isChecking">
          <span v-if="isChecking">Checking...</span>
          <span v-else>Re-Check Student</span>
        </button>
        <button @click="printPage" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition">
          Print Report
        </button>
      </div>
    </div>

    <!-- Loading, Error, and No Results States -->
    <div v-if="loading" class="text-center py-20">
      <p class="text-gray-600">Loading result...</p>
    </div>
    <div v-else-if="error" class="text-center text-red-500 bg-red-50 p-6 rounded-lg">
      <p class="font-semibold">Could not load result.</p>
      <p>{{ error.message || 'Please try again.' }}</p>
    </div>

    <!-- Main Content for Viewing and Printing -->
    <div v-else-if="studentReport" ref="printSection">
      <div class="student-section">
        <!-- Student Info Header -->
        <div class="student-info mb-4 text-left border-b-2 pb-4">
          <p class="text-xl font-bold">{{ studentReport.name }}</p>
          <p><strong>Total Points:</strong> {{ studentReport.points }} / {{ studentReport.totalPoints }}</p>
          <p><strong>Final Mark:</strong> {{ studentReport.mark ?? 'N/A' }}</p>
          <p>
            <strong>Status: </strong>
            <span v-if="isChecking" class="text-blue-600 font-semibold animate-pulse">Checking...</span>
            <span v-else-if="studentReport.status === 'CHECKED'" class="text-green-600 font-semibold">Checked</span>
            <span v-else-if="studentReport.status === 'FAILED'" class="text-red-600 font-semibold">Failed</span>
            <span v-else class="text-gray-600 font-semibold"> {{ studentReport.status }}</span>
          </p>
        </div>

        <!-- Conditional rendering for the results grid -->
        <div v-if="isChecking" class="text-center py-10 border border-gray-200 rounded-lg mt-4">
          <p class="text-gray-600">Grading in progress, results will appear here shortly...</p>
        </div>
        <div v-else-if="!studentReport.answers || studentReport.answers.length === 0" class="text-center py-10 border border-gray-200 rounded-lg mt-4">
          <p class="text-gray-600">No graded results are available for this student yet.</p>
        </div>
        <!-- Results Grid -->
        <div v-else class="grid-container border border-gray-300 rounded-lg shadow">
          <!-- Grid Header -->
          <div class="grid-header grid grid-cols-[72px,minmax(0,1fr),minmax(0,1.5fr),80px] font-medium text-gray-700 bg-gray-100">
            <div class="py-2 px-4 border-b">#</div>
            <div class="py-2 px-4 border-b">Question</div>
            <div class="py-2 px-4 border-b">Student's Answer & Justification</div>
            <div class="py-2 px-4 border-b text-center">Points</div>
          </div>

          <!-- Grid Body -->
          <div class="grid-body">
            <div v-for="(answer, index) in studentReport.answers" :key="answer.number" class="grid-row grid grid-cols-[72px,minmax(0,1fr),minmax(0,1.5fr),80px] border-t">
              <div class="py-2 px-4 font-bold break-words">{{ displayAnswerNumber(answer.number, index) }}</div>
              <div class="py-2 px-4 whitespace-pre-wrap break-words min-w-0">{{ answer.question }}</div>
              <div class="py-2 px-4 whitespace-pre-wrap break-words min-w-0">
                <div class="font-semibold">Student's Answer:</div>
                <p class="mb-2">{{ answer.answer || 'No answer provided' }}</p>
                <div v-if="answer.justification" class="mt-2 pt-2 border-t border-gray-200">
                  <strong class="font-semibold">Justification:</strong> {{ answer.justification }}
                </div>
                <div v-if="answer.correctedText" class="mt-2">
                  <strong class="font-semibold">Corrected Text:</strong> {{ answer.correctedText }}
                </div>
              </div>
              <div class="py-2 px-4 text-center font-medium">{{ answer.points }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '../services/axiosInstance'
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import { useModalStore } from '@/stores/modal' // IMPORT

const studentReport = ref(null)
const loading = ref(true)
const error = ref(null)
const route = useRoute()
const router = useRouter()
const printSection = ref(null)
const stompClient = ref(null)
const modal = useModalStore()

const isChecking = computed(() => {
  return studentReport.value?.status === 'CHECKING'
})

const displayAnswerNumber = (number, index) => {
  if (!number) return index + 1

  const trimmed = String(number).trim()
  const numericSuffix = trimmed.match(/(\d+(?:\.[A-Za-z0-9]+)*)$/)
  return numericSuffix ? numericSuffix[1] : index + 1
}

const fetchData = async () => {
  const testId = route.params.testId
  const studentId = route.params.studentId
  if (!studentId || !testId) {
    error.value = new Error('Test or Student ID is missing from the route.')
    loading.value = false
    return
  }
  loading.value = true
  error.value = null
  try {
    const response = await axios.get(`/results/test/${testId}/students/${studentId}`)
    studentReport.value = response.data
  } catch (err) {
    console.error('Error fetching student result:', err)
    error.value = err
  } finally {
    loading.value = false
  }
}

const recheckStudent = async () => {
  const studentId = route.params.studentId
  // REPLACE native confirm
  const confirmed = await modal.confirm(
      `Are you sure you want to re-check the test for ${studentReport.value?.name}? This will clear existing results and use AI credits.`,
      'Confirm Re-check'
  )

  if (!confirmed) return

  if (studentReport.value) {
    studentReport.value.status = 'CHECKING'
    studentReport.value.answers = []
  }
  error.value = null
  try {
    await axios.post(`/students/${studentId}/check`)
  } catch (err) {
    console.error('Error initiating re-check for student:', err)
    error.value = err
    modal.error('Failed to start the re-checking process.') // REPLACE alert
    fetchData()
  }
}

const connectWebSocket = () => {
  const testId = route.params.testId
  if (!testId) return

  stompClient.value = new Client({
    brokerURL: process.env.VUE_APP_WSS_URL,
    webSocketFactory: () => {
      return new SockJS(process.env.VUE_APP_WS_URL)
    },
    debug: (str) => {
      console.log('STOMP Debug:', str)
    },
    reconnectDelay: 5000
  })

  stompClient.value.onConnect = (frame) => {
    console.log('Connected to WebSocket via STOMP:', frame)
    stompClient.value.subscribe(`/topic/tests/${testId}/status`, (message) => {
      const body = JSON.parse(message.body)
      const studentId = route.params.studentId

      if (body.type === 'student_updated' && body.student?.id === studentId) {
        console.log('Received live update for current student:', body.student)
        studentReport.value = body.student
      }
    })
  }

  stompClient.value.onStompError = (frame) => {
    console.error('Broker reported error: ' + frame.headers.message)
    console.error('Additional details: ' + frame.body)
  }

  stompClient.value.activate()
}

const disconnectWebSocket = () => {
  if (stompClient.value) {
    stompClient.value.deactivate()
    console.log('Deactivated STOMP client.')
  }
}

const goBack = () => {
  const testId = route.params.testId
  if (testId) {
    router.push(`/tests/${testId}`)
  } else {
    router.back()
  }
}

const printPage = () => {
  window.print()
}

onMounted(() => {
  fetchData()
  connectWebSocket()
})

onUnmounted(() => {
  disconnectWebSocket()
})
</script>

<style>
@media print {
  .print-hide {
    display: none;
  }
  .p-6 {
    padding: 0;
  }
  .grid-container {
    box-shadow: none;
    border: none;
  }
}
</style>
